import { useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { PageHero } from '../components/ui/hero.js';
import { WidgetsScene } from '../components/mascot/scenes.js';
import { breadcrumbSchema, jsonLd, seo } from '../lib/seo.js';
import { startUrl } from '../lib/site.js';
import { track } from '../lib/analytics.js';
import {
  WIDGET_BUILDER_PATH,
  WIDGET_CONTENT,
  WIDGET_COUNT_WORD_CAPITALISED,
} from '../content/widgets.js';
import { Measure, Section } from '../components/ui/layout.js';
import { Eyebrow, Heading, Mark, Text } from '../components/ui/type.js';
import { ButtonAnchor, ButtonLink } from '../components/ui/button.js';
import { WidgetCard, WidgetFilter } from '../components/widgets/WidgetCard.js';

/**
 * The widget gallery.
 *
 * ===========================================================================
 * A gallery, which means the widgets are the content
 * ===========================================================================
 * One real preview per layout, at a size you can judge, drawn by the same
 * renderer that runs on customers' websites. Not a card with an icon and a
 * paragraph each: the difference between these layouts is entirely visual, so a
 * page that describes them instead of showing them has made the visitor do the
 * work. The filter draws them too, for the same reason.
 *
 * The filter exists for the visitor who arrived knowing what they want — from
 * a search for "review marquee", say — and would otherwise scroll past five
 * layouts to reach theirs.
 */
export const Route = createFileRoute('/widgets/')({
  head: () => {
    const { meta, links } = seo({
      title: 'Review widgets for your website',
      description:
        `${WIDGET_COUNT_WORD_CAPITALISED} ways to show your reviews on your own site — ${WIDGET_CONTENT.map((widget) => widget.seoName.toLowerCase()).join(', ')}. See each one working, then build yours free.`,
      path: '/widgets',
    });

    return {
      meta,
      links,
      scripts: [
        {
          type: 'application/ld+json',
          children: jsonLd(
            breadcrumbSchema([
              { name: 'Home', path: '/' },
              { name: 'Widgets', path: '/widgets' },
            ])
          ),
        },
      ],
    };
  },
  component: Gallery,
});

const FILTERS = [
  { value: 'all', label: 'All' },
  // `type` is what lets the filter draw each layout's own sketch rather than
  // its name — see `WidgetFilter`.
  ...WIDGET_CONTENT.map((widget) => ({
    value: widget.slug,
    label: widget.seoName,
    type: widget.type,
  })),
];

function Gallery() {
  const [filter, setFilter] = useState('all');

  const shown =
    filter === 'all'
      ? WIDGET_CONTENT
      : WIDGET_CONTENT.filter((widget) => widget.slug === filter);

  return (
    <>
      <PageHero
        trail={[
          { name: 'Home', path: '/' },
          { name: 'Widgets', path: '/widgets' },
        ]}
        art={<WidgetsScene />}
      >
        <Measure>
          <Eyebrow>Widgets</Eyebrow>
          <Heading level={1} size="hero" className="mt-4">
            <Mark>{WIDGET_COUNT_WORD_CAPITALISED} ways</Mark> to show your reviews.
          </Heading>
          <Text size="lead" className="mt-5">
            Every layout below is the real thing — the same code that runs on
            customers&rsquo; websites, drawing real review cards. Pick the shape
            that suits your page, then build it free without an account.
          </Text>

          <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center">
            <ButtonLink to={WIDGET_BUILDER_PATH} size="lg">
              Build a free review widget
            </ButtonLink>
            <ButtonAnchor
              href={startUrl('widgets-gallery')}
              variant="secondary"
              size="lg"
              onClick={() => track('hero_cta_clicked', { surface: 'widgets-gallery' })}
            >
              Start free
            </ButtonAnchor>
          </div>
        </Measure>
      </PageHero>

      <Section size="base">
        <WidgetFilter
          value={filter}
          onChange={(next) => {
            setFilter(next);
            if (next !== 'all') {
              track('widget_selected', { widget: next, surface: 'widgets-gallery' });
            }
          }}
          options={FILTERS}
        />

        {/*
          `aria-live` so a screen-reader user is told the list changed. The
          filter is a radio group and announces its own selection; without this
          nothing says what happened to the content it controls.
        */}
        <p aria-live="polite" className="sr-only">
          Showing {shown.length} of {WIDGET_CONTENT.length} layouts.
        </p>

        {/*
          One per row. The layouts size themselves from viewport breakpoints
          rather than their container, so a two-column gallery renders a
          three-column widget inside a 350px box — see `WidgetCard`.
        */}
        <div className="mt-8 space-y-8">
          {shown.map((widget) => (
            <WidgetCard
              key={widget.slug}
              widget={widget}
              /*
                Filtering to one layout lifts the preview's height cap. That cap
                exists to stop a masonry wall running to three screens inside a
                list of eight — a constraint entirely about neighbours — and
                choosing a single layout is somebody asking to see that one
                properly. Cropping the only thing on the page to protect the
                scroll length of a list that is no longer there would be
                answering a question they did not ask.
              */
              uncapped={shown.length === 1}
            />
          ))}
        </div>
      </Section>

      <Section tone="ink" size="base">
        <Measure>
          <Heading level={2} className="text-white">
            Not sure which one?
          </Heading>
          <Text className="mt-4 text-white/70">
            Open the builder, try them with your own reviews, and change your
            mind as often as you like. Nothing is saved until you want it to be.
          </Text>
          <div className="mt-8">
            <ButtonLink to={WIDGET_BUILDER_PATH} variant="inverse" size="lg">
              Build a free review widget
            </ButtonLink>
          </div>
        </Measure>
      </Section>
    </>
  );
}
